/**
 * Material study — selection, radar tween and image crossfade.
 *
 * No dependency and no framework: six vertices do not need a charting library.
 * Everything here is enhancement. The section renders the first material, its
 * polygon and its spool view from Liquid, so with this file absent or blocked
 * the section is still complete and readable.
 */
(function () {
  'use strict';

  /* Per-axis unit vector scaled by r / 5, so `value * u` is an offset in user
     units from the chart centre. Must stay in step with the literal SVG and the
     Liquid that draws the first polygon. */
  var CX = 195;
  var CY = 149;
  var AXES = [
    [0, -20.4],
    [17.666918, -10.2],
    [17.666918, 10.2],
    [0, 20.4],
    [-17.666918, 10.2],
    [-17.666918, -10.2]
  ];

  var DURATION = 420;
  var FADE = 220;

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Ease-out cubic, per the motion contract. Zero overshoot: an elastic or
     back curve would push a vertex past 5 and read as a value the material
     does not have. */
  function ease(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function points(values) {
    var out = [];
    for (var i = 0; i < AXES.length; i++) {
      out.push(
        (CX + AXES[i][0] * values[i]).toFixed(1) + ',' + (CY + AXES[i][1] * values[i]).toFixed(1)
      );
    }
    return out.join(' ');
  }

  function readValues(button) {
    var raw = (button.getAttribute('data-tms-values') || '').split(',');
    var out = [];
    for (var i = 0; i < 6; i++) {
      var n = parseFloat(raw[i]);
      out.push(isNaN(n) ? 0 : n);
    }
    return out;
  }

  function sum(values) {
    var total = 0;
    for (var i = 0; i < values.length; i++) total += values[i];
    return total;
  }

  function init(root) {
    if (root.getAttribute('data-tms-ready')) return;
    root.setAttribute('data-tms-ready', '1');

    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-tms-material]'));
    if (!buttons.length) return;

    var shape = root.querySelector('[data-tms-shape]');
    var viewButtons = Array.prototype.slice.call(root.querySelectorAll('[data-tms-view-btn]'));

    var active = buttons.filter(function (b) {
      return b.getAttribute('aria-pressed') === 'true';
    })[0] || buttons[0];

    var activeId = active.getAttribute('data-tms-material');
    var activeView = 'spool';
    var current = readValues(active);
    var raf = 0;
    /* Bumped on every media change so a slow image that resolves after the
       user has moved on cannot reveal itself over the newer one. */
    var token = 0;
    var currentShot = root.querySelector('[data-tms-shot]:not([hidden])');

    function tween(target) {
      if (!shape) return;

      cancelAnimationFrame(raf);

      var empty = sum(target) === 0;
      if (!empty) shape.removeAttribute('hidden');

      if (reduced()) {
        current = target.slice();
        shape.setAttribute('points', points(current));
        if (empty) shape.setAttribute('hidden', '');
        return;
      }

      /* Tweens from the CURRENT interpolated vertices, not from the previous
         material's final values, so interrupting mid-flight continues from
         where the shape actually is. Nothing is queued. */
      var from = current.slice();
      var start = performance.now();

      var step = function (now) {
        var progress = Math.min(1, (now - start) / DURATION);
        var e = ease(progress);
        for (var i = 0; i < from.length; i++) {
          current[i] = from[i] + (target[i] - from[i]) * e;
        }
        shape.setAttribute('points', points(current));
        if (progress < 1) {
          raf = requestAnimationFrame(step);
        } else if (empty) {
          shape.setAttribute('hidden', '');
        }
      };

      raf = requestAnimationFrame(step);
    }

    function showShot(materialId, view) {
      var next = root.querySelector(
        '[data-tms-shot="' + materialId + '"][data-tms-view="' + view + '"]'
      );
      if (!next || next === currentShot) return;

      var previous = currentShot;
      var mine = ++token;

      var reveal = function () {
        if (mine !== token) return;

        next.hidden = false;
        next.setAttribute('data-tms-fading', '');
        /* Flush layout so the browser has a zero-opacity frame to start the
           transition from; without it the class is added and removed in the
           same frame and nothing animates. */
        void next.offsetWidth;
        next.removeAttribute('data-tms-fading');

        currentShot = next;
        if (previous && previous !== next) {
          previous.setAttribute('data-tms-fading', '');
          var finish = function () {
            if (previous === currentShot) return;
            previous.hidden = true;
            previous.removeAttribute('data-tms-fading');
          };
          if (reduced()) finish();
          else setTimeout(finish, FADE);
        }

        currentShot = next;
      };

      /* Keep the outgoing image on screen until the incoming one can paint.
         Swapping first would flash the empty stage on a slow connection. */
      var img = next.querySelector('img');
      if (img && !img.complete && typeof img.decode === 'function') {
        img.decode().then(reveal).catch(reveal);
      } else {
        reveal();
      }
    }

    function selectMaterial(button) {
      var id = button.getAttribute('data-tms-material');
      if (id === activeId) return;
      activeId = id;
      root.style.setProperty("--tms-accent", button.getAttribute("data-tms-accent") || "#4b703b");
      var status = root.querySelector("[data-tms-status]");
      if (status) status.textContent = button.textContent.trim();

      buttons.forEach(function (b) {
        b.setAttribute('aria-pressed', String(b === button));
      });

      // Keep the user's numeric-detail preference when comparing materials.
      var visibleDetails = root.querySelector('.tms__specs:not([aria-hidden="true"])');
      var detailsOpen = !!(visibleDetails && visibleDetails.open);
      Array.prototype.forEach.call(root.querySelectorAll('[data-tms-panel]'), function (panel) {
        if (panel.getAttribute('data-tms-panel') === id) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
        if (panel.tagName === 'DETAILS') panel.open = detailsOpen;
      });

      Array.prototype.forEach.call(root.querySelectorAll('[data-tms-cta]'), function (cta) {
        cta.hidden = cta.getAttribute('data-tms-cta') !== id;
      });

      showShot(id, activeView);
      tween(readValues(button));
    }

    function selectView(button) {
      var view = button.getAttribute('data-tms-view-btn');
      if (view === activeView) return;
      activeView = view;
      root.setAttribute("data-tms-mode", view === "properties" ? "properties" : "media");

      viewButtons.forEach(function (b) {
        b.setAttribute('aria-pressed', String(b === button));
      });

      showShot(activeId, view);
    }

    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        selectMaterial(b);
      });
    });

    viewButtons.forEach(function (b) {
      b.addEventListener('click', function () {
        selectView(b);
      });
    });

    var mobile = window.matchMedia('(max-width: 700px)');
    var onLayoutChange = function () {
      if (!mobile.matches && activeView === 'properties') selectView(viewButtons[0]);
    };
    mobile.addEventListener('change', onLayoutChange);
    root.tmsDestroy = function () {
      mobile.removeEventListener('change', onLayoutChange);
      cancelAnimationFrame(raf);
      token++;
    };
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-tms]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme Editor: a re-rendered section is a fresh DOM subtree, so it needs
     initialising, and the outgoing one must stop its animation frame. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    Array.prototype.forEach.call(event.target.querySelectorAll('[data-tms]'), function (root) {
      if (typeof root.tmsDestroy === 'function') root.tmsDestroy();
    });
  });
})();
